// One date format for the whole site.
//
// Content dates come from the admin panel's calendar picker as ISO
// (`2025-09-16`), but older rows were typed by hand or imported and still hold
// `19.04.2023`, `2025-09-16 13:24:15`, `15-may, 2026`, … `formatDate` reads
// every one of these and renders them identically, in the active language:
//
//   uz  16-sentyabr, 2025      ru  16 сентября 2025      en  16 September 2025
//
// Anything it cannot parse is shown as-is rather than blanked out.
import { getLocale } from '@/i18n'

const MONTHS = {
  'uz-Latn': ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avgust', 'sentyabr', 'oktyabr', 'noyabr', 'dekabr'],
  'uz-Cyrl': ['январ', 'феврал', 'март', 'апрел', 'май', 'июн', 'июл', 'август', 'сентябр', 'октябр', 'ноябр', 'декабр'],
  ru: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
}

// Month names as they may appear in hand-typed dates ("15-may, 2026").
const MONTH_LOOKUP = (() => {
  const map = {}
  Object.values(MONTHS).forEach(list => list.forEach((name, i) => { map[name.toLowerCase()] = i + 1 }))
  // Uzbek Latin variants and Russian nominative forms.
  Object.assign(map, {
    sentabr: 9, oktabr: 10, noyabr: 11, dekabr: 12, iyun: 6, iyul: 7,
    январь: 1, февраль: 2, апрель: 4, июнь: 6, июль: 7, сентябрь: 9, октябрь: 10, ноябрь: 11, декабрь: 12
  })
  return map
})()

/** Parse any stored date string into { y, m, d } — or null when it is not a date. */
export function parseDate(value) {
  if (value == null) return null
  const s = String(value).trim()
  if (!s) return null

  let m
  // 2025-09-16, 2025-09-16T10:00:00Z, 2025-09-16 13:24:15, 2025/09/16
  if ((m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T\s].*)?$/.exec(s))) return check(+m[1], +m[2], +m[3])
  // 19.04.2023, 19/04/2023, 19-04-2023
  if ((m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(s))) return check(+m[3], +m[2], +m[1])
  // 15-may, 2026 · 15 may 2026 · 16 сентября 2025 · 16 September 2025
  if ((m = /^(\d{1,2})[-\s]+([^\d,.\s]+)[,.\s]+(\d{4})(?:\s*(?:y|г)\.?)?$/iu.exec(s))) {
    const month = MONTH_LOOKUP[m[2].toLowerCase()]
    if (month) return check(+m[3], month, +m[1])
  }
  return null
}

function check(y, m, d) {
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2200) return null
  return { y, m, d }
}

/** Render a stored date in the site's single format for the given (or current) locale. */
export function formatDate(value, locale = getLocale()) {
  const p = parseDate(value)
  if (!p) return value == null ? '' : String(value)
  const months = MONTHS[locale] || MONTHS['uz-Latn']
  const month = months[p.m - 1]
  if (locale === 'ru') return `${p.d} ${month} ${p.y}`
  if (locale === 'en') return `${p.d} ${month} ${p.y}`
  return `${p.d}-${month}, ${p.y}`
}

/** ISO `YYYY-MM-DD` for a stored date, or '' when unparseable (used for sorting / <time datetime>). */
export function toISODate(value) {
  const p = parseDate(value)
  if (!p) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`
}
